/* User defined models */
import { Payment } from './payment.model';
import { PaymentOption } from './paymentOption.model';

/* User defined enums/constants */
import { QrPaymentCodeMaps } from '../../constants/scheduling.constant';
import { CardFee, QrKey, QrStatus, QrTransactionType } from '../../enums/scheduling.enum';

// Generic QR Objects
export class QrDictionary {
  public key: QrKey;
  public value: string;

  constructor(
    key: QrKey,
    value: string
  ) {
    this.key = key;
    this.value = value;
  }

  static getValueFromAPIDictionary(apiDictionary: any[], key: QrKey): string {
    const item: any = !!apiDictionary ? apiDictionary.find((obj: any) => obj.key === key) : undefined;
    return !!item ? item.value : undefined;
  }
}

export class QrPersonalDetails {
  public firstName: string;
  public lastName: string;

  constructor(
    firstName: string,
    lastName: string
  ) {
    this.firstName = firstName;
    this.lastName = lastName;
  }

  static convertAPIObjectToPersonalDetail(object: any): QrPersonalDetails {
    return new QrPersonalDetails(object.firstName, object.lastName);
  }
}

export class QrLineItem {
  public id: string;
  public amount: number;

  constructor(
    id: string,
    amount: number
  ) {
    this.id = id;
    this.amount = amount;
  }

  static convertAPIObjectToLineItems(object: any[]): QrLineItem[] {
    const lineItems: QrLineItem[] = [];
    object.forEach((obj: any) => {
      lineItems.push(new QrLineItem(obj.id, obj.amount));
    });
    return lineItems;
  }

  static sumLineItems(lineItems: QrLineItem[]): number {
    let amount = 0;
    lineItems.forEach((lineItem: QrLineItem) => {
      amount += lineItem.amount;
    });
    return amount;
  }
}

export class QrStatusUpdate {
  public status: QrStatus;

  constructor(
    status: QrStatus,
  ) {
    this.status = status;
  }

  static convertAPIObjectToStatusUpdate(statusResponseBody: any): QrStatusUpdate {
    return new QrStatusUpdate(
      statusResponseBody.status || statusResponseBody.refundStatus
    );
  }
}

export class QrStatusUpdateObservable {
  public statusUpdate: QrStatusUpdate;
  public status: boolean;

  constructor(
    status: boolean,
    statusUpdate?: QrStatusUpdate
  ) {
    this.status = status;
    this.statusUpdate = statusUpdate;
  }
}

// QR objects used in Init call
export class QrPayment {
  public billTo: QrPersonalDetails;
  public countrySiteCode: string;
  public currency: string;
  public orderSystemCode = 'ProScheduler';
  public paymentType: string;
  public purchasedItems: QrLineItem[];
  public providerRequestData: QrDictionary[];

  constructor(
    billTo: QrPersonalDetails,
    countrySiteCode: string,
    currency: string,
    paymentOption: PaymentOption,
    purchasedItems: QrLineItem[],
    timeout: number,
    successUrl?: string
  ) {
    this.billTo = billTo;
    this.countrySiteCode = countrySiteCode;
    this.currency = currency;
    this.paymentType = paymentOption.name.toLowerCase();
    this.purchasedItems = purchasedItems;
    this.providerRequestData = [
      new QrDictionary(QrKey.successUrl, successUrl || ''),
      new QrDictionary(QrKey.timeout, timeout.toString())
    ];
  }
}

export class QrData {
  public transactionId: string;
  public merchantUrl: string;
  public reasonCode: string;
  public reconciliationId: string;
  public payment: QrPayment;

  constructor(
    initResponseBody: any,
    payment: QrPayment
  ) {
    this.transactionId = initResponseBody.transactionId;
    this.merchantUrl = initResponseBody.merchantUrl;
    this.reasonCode = QrDictionary.getValueFromAPIDictionary(initResponseBody.providerResponseData, QrKey.reasonCode);
    this.reconciliationId = QrDictionary.getValueFromAPIDictionary(initResponseBody.providerResponseData, QrKey.reconciliationId);
    this.payment = payment;
  }
}

export class QrDataObservable {
  public qrData: QrData;
  public status: boolean;

  constructor(
    status: boolean,
    qrData?: QrData
  ) {
    this.status = status;
    this.qrData = qrData;
  }
}

// QR objects used in status calls
export class QrTransaction extends QrStatusUpdate {
  public billTo: QrPersonalDetails;
  public childTransactions: string[];
  public correlationId: string;
  public countrySiteCode: string;
  public createdAt: string;
  public currency: string;
  public merchantId: string;
  public orderSystemCode: string;
  public parentTransactionId: string
  public paymentType: string;
  public providerResponseData: string;
  public providerRequestData: string;
  public purchasedItems: QrLineItem[];
  public reasonCode: string;
  public reconciliationId: string;
  public settledAmount: number;
  public settledCurrency: string;
  public successUrl: string;
  public transactionId: string;
  public transactionTimeout: string;
  public transactionType: QrTransactionType;
    
  constructor(
    status: QrStatus,
    billTo?: QrPersonalDetails,
    childTransactions?: string[],
    correlationId?: string,
    countrySiteCode?: string,
    createdAt?: string,
    currency?: string,
    merchantId?: string,
    orderSystemCode?: string,
    parentTransactionId?: string,
    paymentType?: string,
    providerResponseData?: string,
    providerRequestData?: string,
    purchasedItems?: QrLineItem[],
    settledAmount?: number,
    settledCurrency?: string,
    transactionId?: string,
    transactionType?: QrTransactionType
  ) {
    super(status);

    this.billTo = billTo;
    this.childTransactions = childTransactions;
    this.correlationId = correlationId;
    this.countrySiteCode = countrySiteCode;
    this.createdAt = createdAt
    this.currency = currency;
    this.merchantId = merchantId;
    this.orderSystemCode = orderSystemCode;
    this.parentTransactionId = parentTransactionId;
    this.paymentType = paymentType;
    this.providerResponseData = providerResponseData;
    this.providerRequestData = providerRequestData;
    this.purchasedItems = purchasedItems;
    this.settledAmount = settledAmount;
    this.settledCurrency = settledCurrency;
    this.transactionId = transactionId;
    this.transactionType = transactionType;
  }

  static convertAPIObjectToTransaction(transactionResponseBody: any): QrTransaction {
    return new QrTransaction(
      transactionResponseBody.status,
      QrPersonalDetails.convertAPIObjectToPersonalDetail(transactionResponseBody.billTo),
      transactionResponseBody.childTransactions,
      transactionResponseBody.correlationId,
      transactionResponseBody.countrySiteCode,
      transactionResponseBody.createdAt,
      transactionResponseBody.currency,
      transactionResponseBody.merchantId,
      transactionResponseBody.orderSystemCode,
      transactionResponseBody.parentTransactionId,
      transactionResponseBody.paymentType,
      transactionResponseBody.providerResponseData,
      transactionResponseBody.providerRequestData,
      QrLineItem.convertAPIObjectToLineItems(transactionResponseBody.purchasedItems),
      transactionResponseBody.settledAmount,
      transactionResponseBody.settledCurrency,
      transactionResponseBody.transactionId,
      transactionResponseBody.transactionType
    );
  }
}

export class QrTransactionObservable {
  public statusUpdate: QrTransaction;
  public status: boolean;

  constructor(
    status: boolean,
    statusUpdate?: QrTransaction
  ) {
    this.status = status;
    this.statusUpdate = statusUpdate;
  }
}

// QR objects used in stage calls
export class QrStage {
  public billTo: QrPersonalDetails;
  public currencyCode: string;
  public merchantId: string;
  public paymentTypeCode: string;
  public purchasedItems: QrLineItem[];
  public reasonType: CardFee;
  public salesCorrelationId: string;
  public settlementAmount: number;
  public settlementCurrency: string;
  // workstationCalendarId is optional and not included here as the UI has no concept of it.

  constructor(
    billTo: QrPersonalDetails,
    currencyCode: string,
    merchantId: string,
    paymentTypeCode: string,
    purchasedItems: QrLineItem[],
    reasonType: CardFee,
    salesCorrelationId: string,
    settlementAmount: number,
    settlementCurrency: string
  ) {
    this.billTo = billTo;
    this.currencyCode = currencyCode;
    this.merchantId = merchantId;
    this.paymentTypeCode = paymentTypeCode;
    this.purchasedItems = purchasedItems;
    this.reasonType = reasonType;
    this.salesCorrelationId = salesCorrelationId;
    this.settlementAmount = settlementAmount;
    this.settlementCurrency = settlementCurrency;
  }

  static convertTransactionToStage(
    transaction: QrTransaction,
    reasonType: CardFee
  ): QrStage {
    return new QrStage(
      transaction.billTo,
      transaction.currency,
      transaction.merchantId,
      QrPaymentCodeMaps[transaction.paymentType.toLowerCase()],
      transaction.purchasedItems,
      reasonType,
      transaction.correlationId,
      transaction.settledAmount,
      transaction.settledCurrency
    );
  }
}

// QR objects used in refund calls
export class QrRefund {
  public amount: number;
  public currency: string;

  constructor(
    amount: number,
    currency: string
  ) {
    this.amount = amount;
    this.currency = currency;
  }

  static convertQrTransactionToRefund(qrTransaction: QrTransaction) {
    return new QrRefund(
      qrTransaction.settledAmount,
      qrTransaction.settledCurrency
    );
  }

  static convertPaymentToRefund(payment: Payment) {
    return new QrRefund(
      payment.settlementAmount,
      payment.settlementCurrency
    );
  }

  static convertPaymentToRefundConfirmation(payment: Payment) {
    return new QrRefund(
      payment.amount,
      payment.currency
    );
  }
}

export class QrRefundStage extends QrRefund {
  public refundTransactionId: string;
  public settlementAmount: number;
  public settlementCurrency: string;

  constructor(
    amount: number,
    currency: string,
    refundTransactionId: string,
    settlementAmount: number,
    settlementCurrency: string
  ) {
    super(
      amount,
      currency
    );
    this.refundTransactionId = refundTransactionId;
    this.settlementAmount = settlementAmount;
    this.settlementCurrency = settlementCurrency;
  }

  static convertPaymentToStage(payment: Payment, refundTransactionId: string): QrRefundStage{
    return new QrRefundStage(
      payment.amount,
      payment.currency,
      refundTransactionId,
      payment.settlementAmount,
      payment.settlementCurrency
    );
  }
}

export class QrRefundStatusUpdate extends QrStatusUpdate {
  public id: string;
  public refundTransactionId: string;

  constructor(
    id: string,
    refundTransactionId: string,
    status: QrStatus
  ) {
    super(status);

    this.id = id;
    this.refundTransactionId = refundTransactionId;
  }

  static convertAPIObjectToRefundStatusUpdate(statusResponseBody: any): QrRefundStatusUpdate {
    return new QrRefundStatusUpdate(
      QrDictionary.getValueFromAPIDictionary(statusResponseBody.providerResponseData, QrKey.id),
      statusResponseBody.refundTransactionId,
      statusResponseBody.refundStatus
    );
  }
}

export class QrRefundStatusUpdateObservable {
  public statusUpdate: QrRefundStatusUpdate;
  public status: boolean;

  constructor(
    status: boolean,
    statusUpdate?: QrRefundStatusUpdate
  ) {
    this.status = status;
    this.statusUpdate = statusUpdate;
  }
}
